import { useMemo } from "react";
import {
  Disclosure,
  DisclosureContent,
  DisclosureType,
  SupportedLanguage,
} from "src/components/disclosures/disclosure.types";
import { LegalDocumentRes, useGetCurrentLegalDocumentQuery } from "src/store/features/api/generated";
import { useAuth } from "src/providers/AuthProvider";

function toFrontendDisclosureType(doc: LegalDocumentRes): DisclosureType {
  switch (String(doc.type)) {
    case "state-disclosure":
      return "state-disclosure";
    case "transaction-disclosure":
      return "transaction-disclosure";
    case "withdrawal-disclosure":
      return "withdrawal-disclosure";
    default:
      return "terms-of-business";
  }
}

/** Backend-resolved ISO region (e.g. "CA"); undefined for country/global docs. */
export function getDocumentRegion(doc: LegalDocumentRes): string | undefined {
  const region = doc.jurisdiction?.region;
  return typeof region === "string" && region.trim() ? region.trim() : undefined;
}

/** Map a backend LegalDocumentRes (translations array keyed by `language`) to the frontend `Disclosure`. Null if empty. */
export function legalDocumentToDisclosure(doc: LegalDocumentRes): Disclosure | null {
  const translations = doc.translations ?? [];
  if (translations.length === 0) return null;

  const content = {} as Record<SupportedLanguage, DisclosureContent>;
  for (const t of translations) {
    const { language, ...rest } = t;
    content[language as unknown as SupportedLanguage] = rest as unknown as DisclosureContent;
  }

  const frontendType = toFrontendDisclosureType(doc);
  const region = getDocumentRegion(doc);
  return {
    id: region ? `${frontendType}-${region}` : frontendType,
    type: frontendType,
    // Shown in the signup disclosure list
    name: content.en?.title ?? frontendType,
    required: true,
    region,
    content,
  };
}

export interface UseLegalDisclosureParams {
  /** Backend LegalDocumentType, e.g. "state-disclosure" | "transaction-disclosure". */
  type: string;
  /** Skip the fetch entirely. */
  skip?: boolean;
}

/** Current published doc of `type` → `Disclosure`; jurisdiction resolved server-side, no fallback, skipped until authed. */
export function useLegalDisclosure(params: UseLegalDisclosureParams): {
  disclosure: Disclosure | null;
  /** Raw backend document — for callers needing fields the Disclosure shape drops (e.g. version). */
  document: LegalDocumentRes | null;
  isLoading: boolean;
  isError: boolean;
} {
  const { isAuthenticated } = useAuth();
  const skip = Boolean(params.skip) || !isAuthenticated || !params.type;

  const { data, isLoading, isError } = useGetCurrentLegalDocumentQuery(
    // `type` is a path param after the regenerate; cast until generated arg catches up.
    { type: params.type } as Parameters<typeof useGetCurrentLegalDocumentQuery>[0],
    { skip },
  );

  const disclosure = useMemo<Disclosure | null>(
    () => (data ? legalDocumentToDisclosure(data) : null),
    [data],
  );

  return { disclosure, document: data ?? null, isLoading, isError };
}

/** Deposit disclosure note (per-language) + version, for the deposit flow's noticeTextOverride + acceptance. */
export function useDepositDisclosure(): {
  note?: Partial<Record<SupportedLanguage, string>>;
  version?: string;
  isLoading: boolean;
} {
  const { document, isLoading } = useLegalDisclosure({ type: "deposit-disclosure" });
  const note = useMemo<Partial<Record<SupportedLanguage, string>> | undefined>(() => {
    if (!document?.translations?.length) return undefined;
    const out: Partial<Record<SupportedLanguage, string>> = {};
    for (const t of document.translations) {
      out[t.language as unknown as SupportedLanguage] = t.content;
    }
    return out;
  }, [document]);
  return { note, version: document?.version, isLoading };
}
